/**
 * useDeepLinkSearch
 * 
 * Custom hook to handle deep link search parameters from URL
 * Parses city, region, country, and radius params and triggers geocoding
 * 
 * @param {Object} params - Hook parameters
 * @param {string} params.activeTab - Currently active tab
 * @param {string} params.searchLocation - Current search location string
 * @param {Function} params.setSearchLocation - Search location setter
 * @param {Function} params.setRadius - Radius setter
 * @param {Function} params.geocodeAddress - Function to geocode the address
 * 
 * @returns {void} - Side effects only
 */

import { useEffect, useRef } from 'react'

export function useDeepLinkSearch({
  activeTab,
  searchLocation,
  setSearchLocation,
  setRadius,
  geocodeAddress
}) {
  // Track if we've processed the deep link to avoid repeated geocoding
  const deepLinkProcessedRef = useRef(false)

  // Handle deep link city/region/country search parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const cityParam = params.get('city')
    const regionParam = params.get('region')
    const countryParam = params.get('country')
    const radiusParam = params.get('radius')
    
    // If any location parameter exists, search tab is active, and we haven't processed this yet.
    // Skip city geocoding when an entity deeplink is present — the entity's lat/lng params
    // already center the map correctly, and geocoding the city name would flyTo a different
    // center and zoom out, preventing the entity popup from opening.
    const hasEntityDeepLink = !!new URLSearchParams(window.location.search).get('entity')
    const hasLocationParam = cityParam || regionParam || countryParam
    if (hasLocationParam && activeTab === 'search' && !deepLinkProcessedRef.current && !hasEntityDeepLink) {
      const decodedCity = cityParam ? decodeURIComponent(cityParam.replace(/\+/g, ' ')) : ''
      const decodedRegion = regionParam ? decodeURIComponent(regionParam.replace(/\+/g, ' ')) : ''
      const decodedCountry = countryParam ? decodeURIComponent(countryParam.replace(/\+/g, ' ')) : ''
      
      // Combine city, region, and country for most precise location
      // Build from available parameters (e.g., "Austin, TX, USA" or "Asturias, Spain" or just "Spain")
      const locationParts = []
      if (decodedCity) locationParts.push(decodedCity)
      if (decodedRegion) locationParts.push(decodedRegion)
      if (decodedCountry && decodedCountry !== decodedRegion) locationParts.push(decodedCountry)
      
      const fullLocation = locationParts.join(', ')
      
      // Set radius if provided in URL
      if (radiusParam) {
        const radiusValue = parseInt(radiusParam, 10)
        if (!isNaN(radiusValue) && radiusValue > 0 && radiusValue <= 100) {
          setRadius(radiusValue)
        }
      }
      
      // Mark as processed
      deepLinkProcessedRef.current = true
      
      // Set location first
      setSearchLocation(fullLocation)
    }
  }, [activeTab, setSearchLocation, setRadius])

  // Separate effect to trigger geocoding after searchLocation is set
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hasLocationParam = params.get('city') || params.get('region') || params.get('country')
    
    // Only trigger if we have URL parameters, searchLocation is set, and we marked this as a deep link
    if (hasLocationParam && activeTab === 'search' && searchLocation && deepLinkProcessedRef.current) {
      const timer = setTimeout(() => {
        geocodeAddress()
        // Reset the flag after geocoding so we don't repeat
        deepLinkProcessedRef.current = false
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [searchLocation, activeTab, geocodeAddress])
}
